import React from "react";
import PropTypes from "prop-types";
import { FieldArray, useFormikContext, Field } from "formik";
import useTranslate from "hooks/use-translate-language";
import { formatDate, isValidPhone, isValidZip } from "utils/misc";
import { STATES } from "utils/enum";
import Switch from "components/ui_new/switch";
import { TextInput, SelectInput, PhoneNumberInput } from "components/form_new";
import { DateInputField } from "components/form";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Autocomplete, TextField, InputAdornment, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getAutocompleteStyles } from "components/form_new/component-styles";
import "./styles.scss";

const getInsuranceDisplayName = index => {
  switch (index) {
    case 0:
      return "Primary";
    case 1:
      return "Secondary";
    case 2:
      return "Tertiary";
    default:
      return "";
  }
};

// form_lists/insurance_relationship_types
const guarantorOptions = [
  "Self",
  "Spouse",
  "Child",
  "Other Adult",
  "Unspecified Dependent",
  "Cadaver Donor",
  "Organ Donor",
  "Employee",
  "Life Partner"
].map(x => ({
  text: x,
  value: x
}));

const InsuranceForm = ({ insuranceOptions, patientInsurances, questions }) => {
  const translate = useTranslate();

  const insurnace_change_question = questions.find(
    q => q.id === "insurance_change"
  );

  const { values, errors, setFieldValue } = useFormikContext();

  const disabled = !values.questions?.insurance_change;

  React.useEffect(() => {
    if (values.questions?.insurance_change === false) {
      setFieldValue("insurances", patientInsurances);
    }
  }, [values.questions?.insurance_change]);
  if (!insurnace_change_question) {
    return null;
  }

  return (
    <div>
      <div className="insuranc-container">
        <span style={{ marginRight: "5px" }}>
          {translate("insuranceHasChanged")}
        </span>
        <Switch
          validate={x => (typeof x === "boolean" ? undefined : "Required")}
          name="questions.insurance_change"
          checked={values.questions.insurance_change}
          onChange={e =>
            setFieldValue("questions.insurance_change", e.target.checked)
          }
          inputProps={{ "aria-label": translate("insuranceHasChanged") }}
        />
      </div>
      <FieldArray
        name="insurances"
        render={arrayHelpers => (
          <div style={{ marginTop: 20 }}>
            {values.insurances?.map((_, index) => (
              <div key={index} style={{ padding: 5 }}>
                {errors.insurances && errors.insurances[index] && (
                  <div style={{ color: "red" }}>
                    {errors.insurances[index].insurance_company_id}
                  </div>
                )}
                <Autocomplete
                  disabled={disabled}
                  className="autocomplete"
                  getOptionSelected={(option, value) => {
                    return (
                      option?.insurance_company_id ==
                      value?.insurance_company_id
                    );
                  }}
                  options={insuranceOptions.map(x => ({
                    ...x,
                    insurance_company_id: x.value,
                    insurance_company_name: x.text
                  }))}
                  getOptionLabel={option => option.insurance_company_name}
                  value={values.insurances[index]}
                  onChange={(_, newValue) => {
                    setFieldValue(`insurances.${index}`, {
                      ...values.insurances[index],
                      ...newValue,
                      primary_order: index + 1
                    });
                  }}
                  sx={getAutocompleteStyles(
                    !!errors.insurances?.[index]?.insurance_company_id
                  )}
                  popupIcon={<KeyboardArrowDownIcon />}
                  slotProps={{
                    clearIndicator: {
                      tabIndex: 0,
                      "aria-label": `Clear ${getInsuranceDisplayName(index)} Insurance selection`,
                      title: `Clear ${getInsuranceDisplayName(index)} Insurance selection`,
                      onKeyDown: event => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setFieldValue(`insurances.${index}`, {
                            ...values.insurances[index],
                            insurance_company_id: "",
                            insurance_company_name: ""
                          });
                        }
                      },
                      onClick: () => {
                        setFieldValue(`insurances.${index}`, {
                          ...values.insurances[index],
                          insurance_company_id: "",
                          insurance_company_name: ""
                        });
                      }
                    }
                  }}
                  renderInput={params => {
                    return (
                      <TextField
                        {...params}
                        error={errors.insurances?.[index]?.insurance_company_id}
                        helperText={
                          errors.insurances?.[index]?.insurance_company_id
                        }
                        label={getInsuranceDisplayName(index) + " Insurance"}
                        disabled={disabled}
                        slotProps={
                          disabled
                            ? {}
                            : {
                                input: {
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment
                                      position="start"
                                      style={{
                                        color: "#337abb",
                                        marginLeft: "5px"
                                      }}
                                    >
                                      <SearchIcon />
                                    </InputAdornment>
                                  )
                                }
                              }
                        }
                      />
                    );
                  }}
                />
                <div className="insurance-data">
                  <TextInput
                    name={`insurances.${index}.policy_number`}
                    type="text"
                    label={translate("policyNumber")}
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                    style={{ marginBottom: 10 }}
                  />
                  <TextInput
                    name={`insurances.${index}.group_number`}
                    type="text"
                    label={translate("groupNumber")}
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                    style={{ marginBottom: 10 }}
                  />
                  <PhoneNumberInput
                    name={`insurances.${index}.insurance_phone`}
                    type="tel"
                    disabled={disabled}
                    label={translate("insurancePhoneNumber")}
                    validate={isValidPhone}
                    style={{ marginBottom: 10 }}
                  />

                  <Field
                    name={`insurances.${index}.policy_start_date`}
                    component={DateInputField}
                    label={translate("policyStartDate")}
                    disabled={disabled}
                    style={{ marginBottom: 10 }}
                  />

                  <TextInput
                    name={`insurances.${index}.guarantor_name`}
                    type="text"
                    disabled={disabled}
                    label={translate("guarantorName")}
                    style={{ marginBottom: 10 }}
                  />
                  <SelectInput
                    name={`insurances.${index}.guarantor_relation`}
                    component="select"
                    label={translate("gurantor")}
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                    options={guarantorOptions}
                    IconComponent={KeyboardArrowDownIcon}
                    style={{ marginBottom: 10 }}
                  />
                  <Field
                    name={`insurances.${index}.guarantor_dob`}
                    component={DateInputField}
                    label={translate("guarantorDob")}
                    disabled={disabled}
                    style={{ marginBottom: 10 }}
                  />

                  <TextInput
                    name={`insurances.${index}.insurance_address.street_address`}
                    type="text"
                    disabled={disabled}
                    label="Insurance Street Address"
                    style={{ marginBottom: 10 }}
                  />

                  <TextInput
                    name={`insurances.${index}.insurance_address.street_address2`}
                    type="text"
                    disabled={disabled}
                    label="Insurance Street Address 2"
                    style={{ marginBottom: 10 }}
                  />
                  <TextInput
                    name={`insurances.${index}.insurance_address.city`}
                    type="text"
                    disabled={disabled}
                    label="Insurance City"
                    style={{ marginBottom: 10 }}
                  />
                  <SelectInput
                    name={`insurances.${index}.insurance_address.state`}
                    component="select"
                    disabled={disabled}
                    label="Insurance State"
                    options={STATES}
                    style={{ height: "100%", marginBottom: 10 }}
                    IconComponent={KeyboardArrowDownIcon}
                  />
                  <TextInput
                    name={`insurances.${index}.insurance_address.zip`}
                    type="text"
                    disabled={disabled}
                    label="Insurance Zip"
                    validate={isValidZip}
                    style={{ marginBottom: 10 }}
                  />
                </div>

                <div>
                  {index > 0 && !disabled && (
                    <div
                      style={{
                        display: "flex",
                        flex: 1,
                        justifyContent: "right",
                        alignItems: "center"
                      }}
                    >
                      <Button
                        variant="outlined"
                        aria-label={`Delete ${getInsuranceDisplayName(index)} Insurance`}
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {values.insurances.length == 1 && !disabled && (
              <div
                style={{
                  display: "flex",
                  flex: 1,
                  justifyContent: "right",
                  alignItems: "center",
                  marginTop: "10px"
                }}
              >
                <Button
                  type="button"
                  variant="contained"
                  color="secondary"
                  aria-label="Add Secondary Insurance"
                  onClick={() =>
                    arrayHelpers.push({
                      insurance_company_id: "",
                      insurance_company_name: "",
                      plan_number: "",
                      group_number: "",
                      guarantor_relation: "",
                      policy_start_date: "",
                      primary_order: values.insurances.length + 1
                    })
                  }
                >
                  Add Secondary Insurance
                </Button>
              </div>
            )}
          </div>
        )}
      />
    </div>
  );
};

InsuranceForm.propTypes = {
  patientInsurances: PropTypes.arrayOf(PropTypes.object).isRequired,
  insuranceOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  questions: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default InsuranceForm;
